/**
 * Hook for getting user role level
 * Returns the numeric role level (2, 4, 5, 6, or 8) for the current user
 */

import { useState, useEffect, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { DebugUserLevelContext } from "@/contexts/DebugUserLevelContext";
import { supabaseFetch, buildFilter } from "@/lib/supabaseFetch";

/**
 * Hook to get the current user's role level
 * Returns the numeric role level: 2 (user), 4 (paid), 5 (corporate), 6 (admin), or 8 (super_admin)
 * Defaults to level 2 (user) if role cannot be determined or user is not authenticated
 */
export const useUserRoleLevel = () => {
  // Get override level from context if available (for debug mode)
  const debugContext = useContext(DebugUserLevelContext);
  const overrideLevel = debugContext?.overrideLevel ?? null;
  
  const [roleLevel, setRoleLevel] = useState<number>(2);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  /**
   * Calls the RPC function via REST API (bypasses stuck Supabase client and RLS)
   * Uses direct HTTP call to Supabase REST API
   */
  const fetchRoleLevelViaRPC = async (userId: string): Promise<number> => {
    try {
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      // Get access token from localStorage
      const sessionKey = SUPABASE_URL ? `sb-${SUPABASE_URL.split('//')[1].split('.')[0]}-auth-token` : '';
      const storedSession = sessionKey ? localStorage.getItem(sessionKey) : null;
      let accessToken = SUPABASE_KEY || '';
      
      if (storedSession) {
        try {
          const sessionData = JSON.parse(storedSession);
          if (sessionData.access_token) {
            accessToken = sessionData.access_token;
          }
        } catch (e) {
          console.warn('[useUserRoleLevel] Error parsing session for RPC call:', e);
        }
      }
      
      console.log('[useUserRoleLevel] Calling RPC via REST API for user:', userId);
      
      // Call RPC function via REST API
      const url = `${SUPABASE_URL}/rest/v1/rpc/get_user_role_level`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_KEY || '',
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation',
        },
        body: JSON.stringify({ _user_id: userId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[useUserRoleLevel] RPC REST API error:', {
          status: response.status,
          error: errorText
        });
        return 2;
      }

      const data = await response.json();
      console.log('[useUserRoleLevel] RPC REST API result:', data);
      
      return typeof data === 'number' ? data : 2;
    } catch (error) {
      console.error('[useUserRoleLevel] RPC REST API exception:', error);
      return 2;
    }
  };

  /**
   * Fallback: Fetches role level by querying user_roles table directly
   * Uses supabaseFetch (direct HTTP) to bypass stuck Supabase client
   */
  const fetchRoleLevelDirect = async (userId: string): Promise<number> => {
    try {
      console.log('[useUserRoleLevel] Direct query: Starting fetch for user:', userId);
      console.log('[useUserRoleLevel] Filter being used:', buildFilter({ user_id: userId }));
      
      // Use supabaseFetch (direct HTTP) instead of supabase client to avoid timeouts
      const { data: rolesData, error: rolesError } = await supabaseFetch(
        'user_roles',
        {
          select: 'role',
          filter: buildFilter({ user_id: userId })
        }
      );
      
      console.log('[useUserRoleLevel] Direct query result:', {
        rolesData,
        rolesError,
        count: rolesData?.length || 0,
        hasData: !!rolesData,
        isArray: Array.isArray(rolesData),
        rawData: JSON.stringify(rolesData)
      });

      if (rolesError) {
        console.error('[useUserRoleLevel] Direct query error:', rolesError);
        return 2;
      }

      if (!rolesData || !Array.isArray(rolesData) || rolesData.length === 0) {
        console.log('[useUserRoleLevel] No roles found, defaulting to level 2');
        return 2;
      }

      // Determine role level based on roles (same logic as database function)
      const roles = rolesData.map((r: { role: string }) => r.role);
      console.log('[useUserRoleLevel] User roles found:', roles);
      
      if (roles.includes('super_admin')) {
        console.log('[useUserRoleLevel] Returning level 8 (super_admin)');
        return 8;
      }
      if (roles.includes('admin')) {
        console.log('[useUserRoleLevel] Returning level 6 (admin)');
        return 6;
      }
      if (roles.includes('corporate')) {
        console.log('[useUserRoleLevel] Returning level 5 (corporate)');
        return 5;
      }
      if (roles.includes('paid')) {
        console.log('[useUserRoleLevel] Returning level 4 (paid)');
        return 4;
      }
      
      console.log('[useUserRoleLevel] No matching role, returning level 2');
      return 2;
    } catch (error) {
      console.error('[useUserRoleLevel] Direct query exception:', error);
      return 2;
    }
  };

  /**
   * Fetches the user's role level from the database
   * Uses direct query first (faster, more reliable), with RPC as backup
   */
  const fetchRoleLevel = async (userId: string): Promise<number> => {
    try {
      console.log('[useUserRoleLevel] Fetching role level for user:', userId);
      
      // Try RPC via REST API first (bypasses RLS and stuck client)
      console.log('[useUserRoleLevel] Trying RPC via REST API first...');
      const rpcResult = await fetchRoleLevelViaRPC(userId);
      
      // If RPC returns a valid level, use it
      if (rpcResult !== 2) {
        console.log('[useUserRoleLevel] RPC via REST API succeeded, returning:', rpcResult);
        return rpcResult;
      }
      
      // If RPC returned 2, try direct query as backup
      console.log('[useUserRoleLevel] RPC returned 2, trying direct query as backup...');
      const directResult = await fetchRoleLevelDirect(userId);
      
      // Use direct query result if it's different
      if (directResult !== 2) {
        console.log('[useUserRoleLevel] Direct query found different result, returning:', directResult);
        return directResult;
      }
      
      console.log('[useUserRoleLevel] Both methods returned 2, using RPC result');
      const rpcPromise = supabase.rpc('get_user_role_level', {
        _user_id: userId
      });
      
      const timeoutPromise = new Promise<{ data: number | null; error: { message: string } }>((resolve) => {
        setTimeout(() => {
          console.warn('[useUserRoleLevel] RPC call timed out after 3s, using direct query result');
          resolve({ data: null, error: { message: 'RPC timeout' } });
        }, 3000);
      });

      const result = await Promise.race([rpcPromise, timeoutPromise]);
      const { data, error } = result;

      // Log the full result for debugging
      console.log('[useUserRoleLevel] RPC call result:', {
        data,
        error,
        dataType: typeof data,
        isNumber: typeof data === 'number',
        userId
      });

      if (error) {
        const errorDetails: Record<string, unknown> = {
          error,
          message: error?.message,
          userId
        };
        
        // Only include these if error is a PostgrestError
        if ('code' in error) {
          errorDetails.code = (error as { code?: string }).code;
        }
        if ('details' in error) {
          errorDetails.details = (error as { details?: string }).details;
        }
        if ('hint' in error) {
          errorDetails.hint = (error as { hint?: string }).hint;
        }
        
        console.error('[useUserRoleLevel] Error fetching user role level:', errorDetails);
        
        // If RPC fails or times out, try direct query as fallback
        console.log('[useUserRoleLevel] RPC failed, trying direct query fallback...');
        return await fetchRoleLevelDirect(userId);
      }

      if (data === null || data === undefined) {
        console.warn('[useUserRoleLevel] RPC returned null/undefined, defaulting to level 2');
        return 2;
      }

      const roleLevel = typeof data === 'number' ? data : 2;
      console.log('[useUserRoleLevel] Role level result:', roleLevel, 'for user:', userId);
      
      // Double-check: Also query user_roles directly to verify if we got level 2
      if (roleLevel === 2) {
        console.log('[useUserRoleLevel] Got level 2, double-checking with direct query...');
        const directLevel = await fetchRoleLevelDirect(userId);
        if (directLevel !== 2) {
          console.warn('[useUserRoleLevel] Mismatch! RPC returned 2 but direct query returned:', directLevel);
          return directLevel; // Use the direct query result if it's different
        }
      }
      
      return roleLevel;
    } catch (error) {
      console.error('[useUserRoleLevel] Unexpected error fetching user role level:', {
        error,
        message: error instanceof Error ? error.message : String(error),
        userId
      });
      return 2; // Default to level 2 (user) on error
    }
  };

  useEffect(() => {
    let isMounted = true;
    let failsafe: NodeJS.Timeout | null = null;

    // Failsafe: if role level fetch hangs, clear loading after 5s
    failsafe = setTimeout(() => {
      if (isMounted) {
        console.warn('[useUserRoleLevel] Role level fetch failsafe triggered - clearing loading');
        setLoading(false);
      }
    }, 5000);

    /**
     * Initial fetch of user and role level
     * Uses same localStorage approach as Index.tsx to avoid hanging on getUser()
     */
    const initRoleLevel = async () => {
      try {
        console.log('[useUserRoleLevel] Initializing role level...');
        
        // First, try to read session directly from localStorage (bypasses stuck Supabase client)
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        let currentUser: User | null = null;
        
        if (SUPABASE_URL) {
          const sessionKey = `sb-${SUPABASE_URL.split('//')[1].split('.')[0]}-auth-token`;
          const storedSession = localStorage.getItem(sessionKey);
          
          if (storedSession) {
            try {
              const sessionData = JSON.parse(storedSession);
              if (sessionData.access_token && sessionData.user) {
                console.log('[useUserRoleLevel] Found session in localStorage, user:', sessionData.user.email);
                currentUser = sessionData.user;
              } else {
                console.log('[useUserRoleLevel] Session in localStorage but no user data');
              }
            } catch (e) {
              console.warn('[useUserRoleLevel] Error parsing stored session:', e);
            }
          } else {
            console.log('[useUserRoleLevel] No session found in localStorage');
          }
        }
        
        // Fallback: Try getSession with timeout (in case localStorage doesn't have it)
        if (!currentUser) {
          console.log('[useUserRoleLevel] No session in localStorage, trying getSession()...');
          const sessionPromise = supabase.auth.getSession();
          const timeoutPromise = new Promise<{ data: { session: null }; error: null }>((resolve) => {
            setTimeout(() => {
              console.warn('[useUserRoleLevel] getSession() timed out after 2s');
              resolve({ data: { session: null }, error: null });
            }, 2000);
          });

          const result = await Promise.race([sessionPromise, timeoutPromise]);
          currentUser = result.data?.session?.user ?? null;
          if (currentUser) {
            console.log('[useUserRoleLevel] Got user from getSession():', currentUser.email);
          }
        }
        
        if (!isMounted) {
          console.log('[useUserRoleLevel] Component unmounted, aborting');
          if (failsafe) clearTimeout(failsafe);
          return;
        }

        setUser(currentUser);

        if (currentUser) {
          console.log('[useUserRoleLevel] About to fetch role level for user:', currentUser.id, currentUser.email);
          const level = await fetchRoleLevel(currentUser.id);
          console.log('[useUserRoleLevel] Fetched role level:', level);
          if (isMounted) {
            console.log('[useUserRoleLevel] Setting role level:', level, 'for user:', currentUser.email);
            setRoleLevel(level);
            if (failsafe) clearTimeout(failsafe);
          }
        } else {
          if (isMounted) {
            console.log('[useUserRoleLevel] No user found, setting role level to 2');
            setRoleLevel(2); // Default to level 2 when not authenticated
            if (failsafe) clearTimeout(failsafe);
          }
        }
      } catch (error) {
        console.error('[useUserRoleLevel] Error initializing role level:', error);
        if (isMounted) {
          setRoleLevel(2);
          if (failsafe) clearTimeout(failsafe);
        }
      } finally {
        if (isMounted) {
          console.log('[useUserRoleLevel] Setting loading to false');
          setLoading(false);
          if (failsafe) clearTimeout(failsafe);
        }
      }
    };

    initRoleLevel();

    /**
     * Subscribe to auth state changes to update role level when user logs in/out
     */
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (currentUser) {
          const level = await fetchRoleLevel(currentUser.id);
          if (isMounted) {
            console.log('[useUserRoleLevel] Auth state change - Setting role level:', level, 'for user:', currentUser.email);
            setRoleLevel(level);
            setLoading(false);
          }
        } else {
          if (isMounted) {
            console.log('[useUserRoleLevel] Auth state change - No user, setting role level to 2');
            setRoleLevel(2); // Default to level 2 when not authenticated
            setLoading(false);
          }
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (failsafe) clearTimeout(failsafe);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Return override level if set, otherwise return actual role level
  const finalRoleLevel = overrideLevel !== null ? overrideLevel : roleLevel;

  return { roleLevel: finalRoleLevel, loading, user };
};
